import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { Avatar, Button, Form, Input, message, Spin } from "antd";
import { Link, useNavigate } from "react-router-dom";
import LOGO from "../../assets/fastest-logo.jpg.png";
import { loginSuccess } from "../../redux/slices/userSlice";
import { labUserLogin } from "../../endpoints/API";
import ChangePasswordModal from "./ChangePasswordModal";
import Turnstile from "react-turnstile";

const SITE_KEY = process.env.REACT_APP_SITE_KEY;

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const handleLogin = async (values) => {
    if (!captchaToken) {
      message.warning("Please complete the CAPTCHA before logging in.");
      return;
    }

    const userCredentials = {
      username: values.username,
      password: values.password,
      turnstile_token: captchaToken, 
    };

    try {
      setLoading(true);
      const response = await labUserLogin(userCredentials);
      const data = response?.data;
      setEmail(values.username);

      if (response.statusText === "OK") {
        dispatch(
          loginSuccess({
            user: data.labuser,
            token: data.access,
          })
        );
        message.success("Login successful!");
        navigate("/dashboard");
      } else {
        if (data?.change_password) {
          message.warning(data.detail || "Password expired. Please change your password.");
          setShowChangePwd(true);
        } else {
          message.error(data.message || "Unknown error during login.");
        }
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const changePwd = error?.response?.data?.change_password;

      if (changePwd) {
        message.warning(detail || "Password expired. Please change your password.");
        setShowChangePwd(true);
      } else {
        message.error(detail || "An error occurred during login. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = (changed) => {
    setShowChangePwd(false);
    if (changed) {
      message.info("Please login again with your new password.");
    }
  };

  return (
    <Spin spinning={loading}>
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center border p-6 rounded-lg shadow-xl bg-white w-full max-w-md">
          <Link to="/">
            <Avatar
              shape="square"
              src={LOGO}
              style={{ width: "100%", height: "100px", borderRadius: "8px" }}
            />
          </Link>
          <div className="mt-2 text-gray-500 text-sm md:text-base">
            Welcome Lab User – Sign in with your credentials
          </div>

          <Form
            onFinish={handleLogin}
            layout="vertical"
            style={{ marginTop: "1.5rem" }}
          >
            <Form.Item
              name="username"
              label="Email"
              rules={[{ message: "Please enter your email" }]}
            >
              <Input
                placeholder="Enter your email"
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[{ message: "Please enter your password" }]}
            >
              <Input.Password placeholder="Enter your password" />
            </Form.Item>

            {/* 🧩 Cloudflare Turnstile CAPTCHA */}
            <div className="mb-3 flex justify-center">
              <Turnstile
                sitekey={SITE_KEY}
                onVerify={(token) => setCaptchaToken(token)}
                onExpire={() => setCaptchaToken("")}
                theme="light" // or "dark"
              />
            </div>

            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                Login
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>

      <ChangePasswordModal
        visible={showChangePwd}
        onClose={handleModalClose}
        email={email}
      />
    </Spin>
  );
};

export default Login;
